import { createAction, createAsyncThunk } from "@reduxjs/toolkit";

import Cookies from "js-cookie";
import {
  IAuthTokens,
  ILoginValues,
  IPasswordResetValues,
  IPasswordUpdateValuesWithToken,
  IRegisterResponse,
  IRegisterValues,
} from "src/@types/auth";
import { Roles } from "src/@types/users";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "src/constants/cookiesKeys";
import { NotificationService } from "src/helpers/notifications";
import { RegistrationFields } from "src/page-components/auth/RegistrationForm/types";
import { instance } from "src/services/api-client";
import {
  errorResponsesTrns,
  successfulResponsesTrns,
} from "src/translate/responses";

interface LoginUserParams {
  values: ILoginValues;
  onSuccess?: VoidFunction;
}

export const AUTH_SLICE_NAME = "auth";

export const loginUserAsync = createAsyncThunk(
  `${AUTH_SLICE_NAME}/fetchLogin`,
  async ({ values, onSuccess }: LoginUserParams, { rejectWithValue }) => {
    try {
      const { data } = await instance.post<IAuthTokens>("/user/login/", values);

      Cookies.set(ACCESS_TOKEN, data.access_token);
      Cookies.set(REFRESH_TOKEN, data.refresh_token);
      onSuccess();
      NotificationService.success(successfulResponsesTrns.login);
    } catch ({ response }) {
      const errorText = response?.data?.error;
      NotificationService.error(errorText || errorResponsesTrns.login);
      return rejectWithValue(errorText);
    }
  }
);

interface GoogleLoginParams {
  token: string;
  onSuccess?: VoidFunction;
}
export const googleLoginAsync = createAsyncThunk(
  `${AUTH_SLICE_NAME}/fetchGoogleLogin`,
  async ({ token, onSuccess }: GoogleLoginParams, { rejectWithValue }) => {
    try {
      const { data } = await instance.post<IAuthTokens>("/user/google/", {
        token,
      });

      Cookies.set(ACCESS_TOKEN, data.access_token);
      Cookies.set(REFRESH_TOKEN, data.refresh_token);

      NotificationService.success(successfulResponsesTrns.googleAuth);

      onSuccess();
    } catch ({ response }) {
      const errorText = response?.data?.error;
      NotificationService.error(errorText || errorResponsesTrns.googleAuth);
      return rejectWithValue(errorText);
    }
  }
);

interface RegisterParams {
  values: IRegisterValues;
  onSuccess?: VoidFunction;
}
export const registerAsync = createAsyncThunk(
  `${AUTH_SLICE_NAME}/fetchRegister`,
  async ({ values, onSuccess }: RegisterParams, { rejectWithValue }) => {
    try {
      const {
        email,
        password,
        first_name,
        last_name,
        phone_number,
        founding_docs,
        ...profileInfo
      } = values;

      const { data } = await instance.post<IRegisterResponse>(
        "/user/register/",
        {
          email,
          password,
          first_name,
          last_name,
          phone_number,
          profile: { ...profileInfo },
        }
      );

      // If founding_docs exist, it's an agent (broker) and we upload the documents
      if (founding_docs && founding_docs.length) {
        const formData = new FormData();
        founding_docs.forEach((doc) => {
          formData.append(RegistrationFields.foundingDocs, doc);
        });

        await instance.post(
          `/user/documents/upload-document/?user_id=${data.user_id}&doc_token=${data.documents_token}`,
          formData
        );
      }

      onSuccess();
      NotificationService.success(successfulResponsesTrns.registration);
    } catch ({ response }) {
      const errorText = response?.data?.error;
      NotificationService.error(errorText || errorResponsesTrns.registration);
      return rejectWithValue(errorText);
    }
  }
);
interface ResetPasswordParams {
  values: IPasswordResetValues;
  onSuccess?: VoidFunction;
}
export const resetPasswordAsync = createAsyncThunk(
  `${AUTH_SLICE_NAME}/resetPassword`,
  async (
    { values, onSuccess = () => {} }: ResetPasswordParams,
    { rejectWithValue }
  ) => {
    try {
      await instance.post("/user/request-reset/", values);

      onSuccess();
      NotificationService.success(successfulResponsesTrns.resetPassword);
    } catch ({ response }) {
      const errorText = response?.data?.error;
      NotificationService.error(errorText || errorResponsesTrns.resetPassword);
      return rejectWithValue(errorText);
    }
  }
);
interface UpdatePasswordParams {
  values: IPasswordUpdateValuesWithToken;
  onSuccess?: VoidFunction;
}
export const updatePasswordAsync = createAsyncThunk(
  `${AUTH_SLICE_NAME}/updatePassword`,
  async (
    { values, onSuccess = () => {} }: UpdatePasswordParams,
    { rejectWithValue }
  ) => {
    try {
      await instance.post<IAuthTokens>("/user/confirm-reset/", values);
      onSuccess();
      NotificationService.success(successfulResponsesTrns.updatePassword);
    } catch ({ response }) {
      const errorText = response?.data?.error;
      NotificationService.error(errorText || errorResponsesTrns.updatePassword);
      return rejectWithValue(errorText);
    }
  }
);

export const logout = createAction(`${AUTH_SLICE_NAME}/logout`);
